import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import InputBase from '@material-ui/core/InputBase';
import Alert from '@material-ui/lab/Alert';
import { useTheme } from '@material-ui/core/styles';
import { jwtDecode } from 'jwt-decode';
import { fetchEmailData } from '../../utils/service';
import { clearAllUserSession, markSessionLoginAtNow, savePcAuthTokens, consumeIntendedRoute, readIntendedRoute, isSafePostLoginRoute } from '../../auth/pcAuthBrowserFlow';
import {
  isAllowedOnCurrentHost,
  TESTPORTAL_ACCESS_DENIED_MESSAGE,
} from '../../auth/testPortalAccess';
import { getBrandedLoginRouteFromSearch } from '../Login/cidLoginRouting';
import ShieldLogo from './Shield.png';
import WhiteShield from '../../Assets/Logos/WhiteShield.png';
import './EmailLogin.css';

const DOMAIN_LOGIN_CONFIG = {
  'duracell.com': { type: 'microsoft', route: '/Duracell' },
  'phishcloudinc.onmicrosoft.com': { type: 'microsoft', route: '/PhishCloudLogin' },
  'phishcloud.com': { type: 'google-only' },
};

const normalizeEmail = (value) => (value || '').trim().toLowerCase();
const getDomain = (value) => {
  const [, domain] = normalizeEmail(value).split('@');
  return domain || '';
};

const EmailLogin = () => {

  const theme = useTheme();
  const isDarkMode = theme.palette.type === 'dark';

  const history = useHistory();
  const [email, setEmail] = useState('');
  const [coolDown, setCoolDown] = useState(false);
  const [emailNotFound, setEmailNotFound] = useState(false);
  const [queryCount, setQueryCount] = useState(0);
  const [showGenericOptions, setShowGenericOptions] = useState(false);
  const [validatedEmail, setValidatedEmail] = useState('');
  const [genericLoginError, setGenericLoginError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleOnly, setGoogleOnly] = useState(false);
  const pcAuthGoogleStartUrl = process.env.REACT_APP_PC_AUTH_GOOGLE_START_URL;
  const pcAuthStartUrl = process.env.REACT_APP_PC_AUTH_START_URL || '/auth/start';
  const pcAuthPasswordLoginUrl = process.env.REACT_APP_PC_AUTH_PASSWORD_LOGIN_URL || '/auth/login';
  const whoamiInitialCheckUrl =
    process.env.REACT_APP_WHOAMI_INITIAL_CHECK_URL || 'https://wami-v2.phishcloud.io/api/initial_check';

  const passwordMeetsRequirements = (pw) =>
    pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw);

  useEffect(() => {
    const brandedRoute = getBrandedLoginRouteFromSearch(window.location.search);
    if (brandedRoute && window.location.pathname !== brandedRoute) {
      history.replace(brandedRoute);
    }
  }, [history]);

  useEffect(() => {
    const storedQueryCount = localStorage.getItem('emailQueryCount');
    if (storedQueryCount) {
      setQueryCount(parseInt(storedQueryCount, 10));
    }

    const cooldownExpiration = localStorage.getItem('emailQueryCooldownExpiration');
    if (cooldownExpiration && Date.now() < parseInt(cooldownExpiration, 10) && parseInt(storedQueryCount || '0', 10) >= 5) {
      setCoolDown(true);
    } else {
      setCoolDown(false);
      localStorage.setItem('emailQueryCount', 0);
      localStorage.removeItem('emailQueryCooldownExpiration');
    }
  }, []);

  useEffect(() => {
    if (queryCount >= 5) {
      setCoolDown(true);
    }
  }, [queryCount]);

  const resetGenericOptions = () => {
    setShowGenericOptions(false);
    setValidatedEmail('');
    setGenericLoginError('');
    setPassword('');
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswordInput(false);
    setShowPassword(false);
    setGoogleOnly(false);
  };

  const showTooManyError = () => {
    if (coolDown === true) {
      return (
        <div>
          <Alert severity="error">
            Too many failed login attempts - Please wait 30 minutes and try again, or contact support
          </Alert>
        </div>
      );
    }
  };

  const showEmailNotFoundError = () => {
    if (emailNotFound === true) {
      return (
        <div>
          <Alert severity="error">This email is not registered with PhishCloud. Contact your admin if you think this is in error.</Alert>
        </div>
      );
    }
  };

  const getEmailResult = useQuery(
    ['getEmailResult', email],
    async () => {
      const data = {
        email: normalizeEmail(email),
      };

      return fetchEmailData(whoamiInitialCheckUrl, data);
    },
    {
      enabled: false,
      onSuccess: (response) => {
        decodeJwtToken(response.response);
      },
    },
  );

  const submitEmail = (event) => {
    event.preventDefault();

    if (coolDown) {
      return;
    }

    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail || localStorage.getItem('emailQueryCount') > 4) {
      return;
    }

    setEmail(normalizedEmail);
    setEmailNotFound(false);
    setGenericLoginError('');
    setPasswordError('');
    setPasswordSuccess('');
    getEmailResult.refetch();
  };


  function routeByDomain(normalizedEmail, loginPage) {
    const domain = getDomain(normalizedEmail);
    const config = DOMAIN_LOGIN_CONFIG[domain];

    if (config?.type === 'microsoft' && config.route) {
      try { sessionStorage.setItem('pcValidatedLoginEmail', normalizedEmail); } catch (_) {}
      history.push(config.route);
      return;
    }

    setValidatedEmail(normalizedEmail);
    setShowGenericOptions(true);
    setGoogleOnly(config?.type === 'google-only');
  }

  function decodeJwtToken(enteredToken) {
    try {
      const decodedPayload = jwtDecode(enteredToken);
      if (decodedPayload.status_code === 400) {
        setEmailNotFound(true);
        resetGenericOptions();
        updateQueryCount();
        return;
      }

      setEmailNotFound(false);
      routeByDomain(normalizeEmail(email), decodedPayload.login_page || '');
    } catch (error) {
      console.error('Error:', error.message);
      setEmailNotFound(true);
      resetGenericOptions();
    }
  }

  const updateQueryCount = () => {
    const newCount = queryCount + 1;
    localStorage.setItem('emailQueryCount', newCount);
    setQueryCount(newCount);

    if (newCount === 1) {
      const expirationTime = Date.now() + 30 * 60 * 1000;
      localStorage.setItem('emailQueryCooldownExpiration', expirationTime);
    }

    if (newCount >= 5) {
      setCoolDown(true);
    }
  };

  const startConfiguredGoogleSso = () => {
    if (!pcAuthGoogleStartUrl) {
      return false;
    }

    try {
      const baseUrl = pcAuthGoogleStartUrl.startsWith('http')
        ? new URL(pcAuthGoogleStartUrl)
        : new URL(pcAuthGoogleStartUrl, window.location.origin);

      baseUrl.searchParams.set('email', validatedEmail);
      const intendedRoute = readIntendedRoute();
      if (isSafePostLoginRoute(intendedRoute)) {
        baseUrl.searchParams.set('returnTo', intendedRoute);
      }
      window.location.assign(baseUrl.toString());
      return true;
    } catch (error) {
      setGenericLoginError('Google sign-in endpoint configuration is invalid.');
      return false;
    }
  };

  const startPcAuthGoogleSso = () => {
    if (!pcAuthStartUrl) {
      return false;
    }

    try {
      const baseUrl = pcAuthStartUrl.startsWith('http')
        ? new URL(pcAuthStartUrl)
        : new URL(pcAuthStartUrl, window.location.origin);

      baseUrl.searchParams.set('provider', 'google');
      if (validatedEmail) {
        baseUrl.searchParams.set('email', validatedEmail);
      }
      const intendedRoute = readIntendedRoute();
      if (isSafePostLoginRoute(intendedRoute)) {
        baseUrl.searchParams.set('returnTo', intendedRoute);
      }

      window.location.assign(baseUrl.toString());
      return true;
    } catch (error) {
      setGenericLoginError('Google sign-in endpoint configuration is invalid.');
      return false;
    }
  };

  const handleGoogleSignIn = () => {
    setGenericLoginError('');
    if (startConfiguredGoogleSso()) {
      return;
    }

    if (startPcAuthGoogleSso()) {
      return;
    }
    setGenericLoginError('Unable to start Google sign-in. Please try again.');
  };

  const handlePasswordSignIn = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!password) {
      setPasswordError('Enter your password to continue.');
      return;
    }

    setIsPasswordSubmitting(true);
    try {
      const response = await fetch(pcAuthPasswordLoginUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: validatedEmail,
          password,
        }),
      });

      const responseType = response.headers.get('content-type') || '';
      const data = responseType.includes('application/json') ? await response.json() : {};

      if (!response.ok) {
        setPasswordError(data.message || data.error || 'Password sign-in failed. Please try again.');
        return;
      }

      // Clear stale session data from any previous user before establishing new session.
      clearAllUserSession();

      // testportal-only allowlist (prod/localhost unrestricted).
      if (!isAllowedOnCurrentHost(validatedEmail || data)) {
        setPasswordError(TESTPORTAL_ACCESS_DENIED_MESSAGE);
        return;
      }

      // Store the new tokens from password login.
      const accessToken = data.accessToken || data.access_token || '';
      const refreshToken = data.refreshToken || data.refresh_token || '';
      if (accessToken && refreshToken) {
        savePcAuthTokens({ accessToken, refreshToken });
        markSessionLoginAtNow();
      }

      try { sessionStorage.setItem('pcAuthLoginMethod', 'password'); } catch (_) {}

      const redirectUrl = data.redirectUrl || data.redirect_uri || data.nextUrl;

      // Prefer the deep link the user originally tried to reach before login.
      const intendedRoute = consumeIntendedRoute();
      if (intendedRoute) {
        window.location.assign(intendedRoute);
        return;
      }

      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      // Password login returned tokens — redirect to dashboard.
      if (accessToken) {
        window.location.assign('/Dashboard/History');
        return;
      }

      setPasswordSuccess('Password sign-in request accepted.');
    } catch (error) {
      setPasswordError('Unable to reach password sign-in endpoint.');
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const pcAuthForgotPasswordUrl = process.env.REACT_APP_PC_AUTH_FORGOT_PASSWORD_URL || '/auth/password/forgot';

  const handleForgotPassword = async () => {
    setForgotError('');
    setForgotMessage('');
    if (!validatedEmail) {
      setForgotError('Please enter your email first.');
      return;
    }
    setIsForgotSubmitting(true);
    try {
      await fetch(pcAuthForgotPasswordUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: validatedEmail,
          returnTo: `${window.location.origin}/PortalSignIn`,
        }),
      });
    } catch (_) {
      // Best effort — always show the same message for anti-enumeration.
    }
    setForgotMessage('If an account exists for that email, a password reset link has been sent.');
    setIsForgotSubmitting(false);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setEmailNotFound(false);
  };

  return (
    <div className="loginPageWrapper">
      <div className="loginContainer">
      {/* Left marketing panel */}
      <div className="loginLeftPanel">
        <div className="leftPanelContent">
          <div className="leftPanelWordmark">PHISH360°</div>

          <div className="leftPanelHero">
            <h1>COMMAND CENTER</h1>
            <p>Real-Time Human Threat Intelligence</p>
          </div>

          <div className="leftPanelFeatures">
            <div className="featureItem">
              <div className="featureIconWrap">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <div>
                <div className="featureTitle">REAL-TIME VISIBILITY</div>
                <div className="featureDesc">See human-layer threats as they happen across your organization.</div>
              </div>
            </div>
            <div className="featureItem">
              <div className="featureIconWrap">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <div>
                <div className="featureTitle">OPERATIONAL DEFENSE</div>
                <div className="featureDesc">Empower your people with real-time intelligence and contextual awareness.</div>
              </div>
            </div>
            <div className="featureItem">
              <div className="featureIconWrap">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="14" width="4" height="7" rx="1"/><rect x="10" y="9" width="4" height="12" rx="1"/><rect x="16" y="4" width="4" height="17" rx="1"/>
                </svg>
              </div>
              <div>
                <div className="featureTitle">MEASURABLE IMPACT</div>
                <div className="featureDesc">Reduce risk, strengthen behavior, and drive operational resilience.</div>
              </div>
            </div>
          </div>

          <div className="leftPanelTagline">
            HUMAN AWARENESS.<br />
            <span>OPERATIONAL RESILIENCE.</span>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="loginRightPanel">
        <div className="loginFormCard">
          <div className="formCardHeader">
            <img src={isDarkMode ? WhiteShield : ShieldLogo} width={56} alt="PhishCloud Shield" />
            <h2>Welcome</h2>
            <p>Sign in to your PHISH360° Command Center</p>
          </div>

          {showTooManyError()}
          {showEmailNotFoundError()}

          <form onSubmit={submitEmail} className="loginForm">
            {showGenericOptions ? (
              <div className="lockedEmailSection">
                <label className="formLabel">Email</label>
                <div className="lockedEmailValue">{validatedEmail}</div>
              </div>
            ) : (
              <div className="formGroup">
                <label className="formLabel" htmlFor="email">Email</label>
                <div className="formInputWrap">
                  <svg className="formInputIcon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <InputBase
                    className="formInput"
                    type="text"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                  />
                </div>
              </div>
            )}
            {!showGenericOptions && (
              <button type="submit" id="emailLoginButton" className="formSubmitBtn">
                Sign In
              </button>
            )}
          </form>

          {showGenericOptions && (
            <div className="genericLoginSection">
              <div className="genericLoginDivider">
                <span>{googleOnly ? 'Sign in with your Google account' : 'Choose one option to log in'}</span>
              </div>
              {!showPasswordInput && (
                <div className="loginMethodButtons">
                  <button type="button" id="googleLoginButton" className="loginMethodBtn googleMethodBtn" onClick={handleGoogleSignIn}>
                    <svg className="googleLogo" viewBox="0 0 48 48" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.04 24.04 0 0 0 0 21.56l7.98-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    {googleOnly ? 'Sign in with Google' : 'Google'}
                  </button>
                  {!googleOnly && (
                    <button type="button" className="loginMethodBtn emailMethodBtn" onClick={() => setShowPasswordInput(true)}>
                      Email
                    </button>
                  )}
                </div>
              )}
              {showPasswordInput && (
                <div className="passwordSection">
                  <div className="passwordInputContainer">
                    <InputBase
                      className="formInput"
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && passwordMeetsRequirements(password)) {
                          handlePasswordSignIn();
                        }
                      }}
                    />
                    <button type="button" className="passwordToggleBtn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? (
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                    <Tooltip title="At least 8 characters, one uppercase letter, one lowercase letter, and one number" arrow placement="right" classes={{ tooltip: 'passwordTooltip' }}>
                      <span className="passwordInfoIcon">ⓘ</span>
                    </Tooltip>
                  </div>
                  <button
                    type="button"
                    id="passwordLoginButton"
                    className={`formSubmitBtn ${passwordMeetsRequirements(password) ? '' : 'formSubmitBtnDisabled'}`}
                    onClick={handlePasswordSignIn}
                    disabled={!passwordMeetsRequirements(password) || isPasswordSubmitting}
                  >
                    {isPasswordSubmitting ? 'Signing in...' : 'LOGIN'}
                  </button>
                  <div className="forgotPasswordContainer">
                    <button type="button" className="forgotPasswordLink" onClick={handleForgotPassword} disabled={isForgotSubmitting}>
                      {isForgotSubmitting ? 'Sending...' : 'Forgot password?'}
                    </button>
                  </div>
                  <div className="backToMethodsContainer">
                    <button type="button" className="backToMethodsLink" onClick={() => { setShowPasswordInput(false); setPassword(''); setShowPassword(false); }}>
                      ← Back to sign-in options
                    </button>
                  </div>
                </div>
              )}
              {forgotMessage && <div className="optionStatusMessage"><Alert severity="info">{forgotMessage}</Alert></div>}
              {forgotError && <div className="optionStatusMessage"><Alert severity="error">{forgotError}</Alert></div>}
              {genericLoginError && <div className="optionStatusMessage"><Alert severity="error">{genericLoginError}</Alert></div>}
              {passwordError && <div className="optionStatusMessage"><Alert severity="error">{passwordError}</Alert></div>}
              {passwordSuccess && <div className="optionStatusMessage"><Alert severity="success">{passwordSuccess}</Alert></div>}
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button type="button" className="backToMethodsLink" onClick={() => { resetGenericOptions(); setEmail(''); setEmailNotFound(false); }}>
                  ← Back to login
                </button>
              </div>
            </div>
          )}

          <div className="formCardFooter">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span>Enterprise-grade security. Built for operational resilience.</span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
export default EmailLogin;
